import React, { useState, useEffect } from 'react';
import {
  FiCalendar,
  FiMapPin,
  FiUsers,
  FiAlertCircle,
  FiDownload,
  FiX,
  FiLoader,
  FiCheckCircle,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiInfo,
  FiClock,
  FiStar,
  FiHome,
  FiTruck,
  FiPackage,
  FiMessageSquare,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../../../Api';
import './UpcomingHoneymoon.css';

// ─── Custom hook for Razorpay payment (inline) ──────────────────
const useRazorpayPayment = () => {
  const [paymentStep, setPaymentStep] = useState('idle');
  const [paymentError, setPaymentError] = useState(null);

  const initiatePayment = async (bookingId, amount, prefill, onSuccess, onDismiss) => {
    setPaymentStep('creating');
    setPaymentError(null);
    try {
      const token = localStorage.getItem('accessToken');
      const orderResponse = await api.post(
        `/honeymoon/bookings/${bookingId}/razorpay-order`,
        { amount, payment_type: 'full' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!orderResponse.data.status) {
        throw new Error(orderResponse.data.message || 'Failed to create payment order.');
      }
      const order = orderResponse.data.data;
      setPaymentStep('processing');

      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: 'Honeymoon Booking',
        description: `Booking #${order.receipt}`,
        order_id: order.order_id,
        prefill: {
          name: prefill.name || '',
          email: prefill.email || '',
          contact: prefill.contact || '',
        },
        theme: { color: '#c9a227' },
        modal: {
          ondismiss: () => {
            setPaymentStep('idle');
            setPaymentError('Payment was cancelled.');
            if (onDismiss) onDismiss();
          },
        },
        handler: async (response) => {
          try {
            const verifyResponse = await api.post(
              `/honeymoon/bookings/${bookingId}/razorpay-verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (verifyResponse.data.status) {
              setPaymentStep('completed');
              if (onSuccess) onSuccess(verifyResponse.data.data);
            } else {
              throw new Error(verifyResponse.data.message || 'Verification failed.');
            }
          } catch (err) {
            setPaymentStep('failed');
            setPaymentError(err.message);
          }
        },
      };
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      setPaymentStep('failed');
      setPaymentError(err.message);
    }
  };

  const resetPayment = () => {
    setPaymentStep('idle');
    setPaymentError(null);
  };

  return { paymentStep, paymentError, initiatePayment, resetPayment };
};

// ─── Main Component ─────────────────────────────────────────────────
const HoneymoonBookingHistory = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Detail modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const navigate = useNavigate();
  const { paymentStep, paymentError, initiatePayment, resetPayment } = useRazorpayPayment();

  // ── Fetch bookings ──
  const fetchBookings = async (status = '', paymentStatus = '') => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }
      const params = {};
      if (status) params.status = status;
      if (paymentStatus) params.payment_status = paymentStatus;
      const response = await api.get('/honeymoon/bookings/mine', {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = response.data;
      if (result.status) {
        setBookings(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to fetch bookings.');
      }
    } catch (err) {
      setError(err.message || 'Could not load bookings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [bookings]);

  // ── Pagination ──
  const totalPages = Math.ceil(bookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedBookings = bookings.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ── Helpers ──
  const formatPrice = (price) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  const formatDateTime = (dateStr) =>
    new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const getStatusBadge = (paymentStatus, bookingStatus) => {
    if (bookingStatus === 'cancelled') {
      return <span className="status-badge status-cancelled">Cancelled</span>;
    }
    const statusMap = {
      pending: { label: 'Pending Payment', class: 'status-pending' },
      paid: { label: 'Paid', class: 'status-paid' },
      confirmed: { label: 'Confirmed', class: 'status-confirmed' },
      completed: { label: 'Completed', class: 'status-completed' },
      failed: { label: 'Failed', class: 'status-failed' },
    };
    const s = statusMap[paymentStatus] || { label: paymentStatus, class: 'status-unknown' };
    return <span className={`status-badge ${s.class}`}>{s.label}</span>;
  };

  // ── Detail Modal Handlers ──
  const handleViewDetails = async (booking) => {
    setSelectedBooking(booking);
    setShowDetailModal(true);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.get(`/honeymoon/bookings/${booking.booking_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status) {
        setDetailData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load booking details.');
      }
    } catch (err) {
      setError(err.message || 'Could not load booking details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setDetailData(null);
    setSelectedBooking(null);
  };

  // ── Payment Handlers ──
  const handlePayNow = (booking) => {
    setSelectedBooking(booking);
    setShowPaymentModal(true);
    resetPayment();
  };

  const onPaymentSuccess = () => {
    setTimeout(() => {
      fetchBookings();
      setShowPaymentModal(false);
      setSelectedBooking(null);
    }, 2000);
  };

  const onPaymentDismiss = () => {
    setShowPaymentModal(false);
    setSelectedBooking(null);
  };

  const handleInitiatePayment = () => {
    if (!selectedBooking) return;
    const amount = selectedBooking.balance_due || selectedBooking.grand_total || 0;
    let userData = {};
    try {
      const customer = JSON.parse(localStorage.getItem('customer') || '{}');
      userData = customer;
    } catch (e) {}
    const prefill = {
      name: userData.name || '',
      email: userData.email || '',
      contact: userData.phone || '',
    };
    initiatePayment(
      selectedBooking.booking_id,
      amount,
      prefill,
      onPaymentSuccess,
      onPaymentDismiss
    );
  };

  // ── Cancel Handlers ──
  const handleCancelClick = (booking) => {
    setSelectedBooking(booking);
    setCancelReason('');
    setShowCancelModal(true);
    // If detail modal is open, close it
    if (showDetailModal) closeDetailModal();
  };

  const confirmCancel = async () => {
    if (!selectedBooking) return;
    setCancelling(true);
    try {
      const token = localStorage.getItem('accessToken');
      const payload = { reason: cancelReason.trim() || 'No reason provided' };
      const response = await api.post(
        `/honeymoon/bookings/${selectedBooking.booking_id}/cancel`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = response.data;
      if (result.status) {
        fetchBookings();
        setShowCancelModal(false);
        setSelectedBooking(null);
        if (showDetailModal) closeDetailModal();
      } else {
        throw new Error(result.message || 'Cancellation failed.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  // ── Invoice Download (placeholder) ──
  const handleDownloadInvoice = (booking) => {
    alert(`Download invoice for booking #${booking.booking_code}`);
  };

  // ── Render ──
  if (loading) {
    return (
      <div className="booking-history-page">
        <div className="container-custom">
          <div className="booking-history-loader">
            <FiLoader className="spinner" />
            <p>Loading your honeymoon bookings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-history-page">
        <div className="container-custom">
          <div className="booking-history-error">
            <FiAlertCircle className="error-icon" />
            <h3>Error</h3>
            <p>{error}</p>
            <button className="btn-retry" onClick={() => fetchBookings()}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-history-page">
      <div className="container-custom">
        <div className="booking-history-header">
          <h1>My Honeymoon Bookings</h1>
          <p className="subtitle">View and manage your romantic getaway bookings</p>
        </div>

        {bookings.length === 0 ? (
          <div className="booking-empty">
            <FiAlertCircle className="empty-icon" />
            <h3>No honeymoon bookings found</h3>
            <p>Start planning your dream honeymoon today.</p>
            <button className="btn-primary" onClick={() => navigate('/honeymoon-packages')}>
              Explore Honeymoon Packages
            </button>
          </div>
        ) : (
          <>
            <div className="booking-grid">
              {paginatedBookings.map((booking) => (
                <div
                  className="booking-card"
                  key={booking.booking_id}
                  onClick={() => handleViewDetails(booking)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="booking-card-header">
                    <div className="booking-number">
                      <span className="booking-code">#{booking.booking_code}</span>
                      {getStatusBadge(booking.payment_status, booking.booking_status)}
                    </div>
                    <div className="booking-date">
                      <FiCalendar />
                      <span>{formatDate(booking.created_at)}</span>
                    </div>
                  </div>
                  <div className="booking-card-body">
                    <h3 className="booking-package">{booking.package?.name || 'Honeymoon Package'}</h3>
                    <p className="booking-plan">
                      <FiInfo />
                      <span>{booking.plan?.plan_name || 'Plan'} ({booking.plan?.tier || 'Standard'})</span>
                    </p>
                    <div className="booking-meta">
                      <span>
                        <FiCalendar /> Tour: {formatDate(booking.tour_start_date)} - {formatDate(booking.tour_end_date)}
                      </span>
                      <span>
                        <FiUsers /> {booking.pax_count || 2} guests
                      </span>
                    </div>
                    <div className="booking-price">
                      <div className="booking-total">
                        <span>Total</span>
                        <strong>{formatPrice(booking.grand_total)}</strong>
                      </div>
                      {booking.balance_due > 0 && booking.balance_due !== booking.grand_total && (
                        <div className="booking-balance">
                          <span>Balance Due</span>
                          <span>{formatPrice(booking.balance_due)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="booking-card-actions" onClick={(e) => e.stopPropagation()}>
                    {booking.booking_status === 'cancelled' ? (
                      <span className="cancelled-label">Booking Cancelled</span>
                    ) : (
                      <>
                        {booking.payment_status === 'pending' && (
                          <>
                            <button
                              className="btn-pay"
                              onClick={() => handlePayNow(booking)}
                            >
                              Pay Now
                            </button>
                            <button
                              className="btn-cancel"
                              onClick={() => handleCancelClick(booking)}
                            >
                              <FiTrash2 /> Cancel
                            </button>
                          </>
                        )}
                        {(booking.payment_status === 'paid' ||
                          booking.payment_status === 'confirmed' ||
                          booking.payment_status === 'completed') && (
                          <button
                            className="btn-invoice"
                            onClick={() => handleDownloadInvoice(booking)}
                          >
                            <FiDownload /> Download Invoice
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination-container">
                <button
                  className="pagination-btn"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <FiChevronLeft />
                </button>
                <div className="pagination-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      className={`pagination-number ${page === currentPage ? 'active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  className="pagination-btn"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <FiChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Detail Modal ──────────────────────────────────────────── */}
      {showDetailModal && (
        <div className="detail-modal-overlay" onClick={closeDetailModal}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <button className="detail-modal-close" onClick={closeDetailModal}>
              <FiX />
            </button>
            <div className="detail-modal-header">
              <h2>Booking Details</h2>
              <p>#{detailData?.booking_code || selectedBooking?.booking_code}</p>
            </div>

            {detailLoading ? (
              <div className="detail-loading">
                <FiLoader className="spinner" />
                <p>Loading booking details...</p>
              </div>
            ) : detailData ? (
              <div className="detail-content">
                {/* Basic Info */}
                <div className="detail-section">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Package</span>
                      <span className="detail-value">{detailData.package?.name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Plan</span>
                      <span className="detail-value">{detailData.plan?.plan_name} ({detailData.plan?.tier})</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status</span>
                      <span className="detail-value">{getStatusBadge(detailData.payment_status, detailData.booking_status)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Tour Dates</span>
                      <span className="detail-value">{formatDate(detailData.tour_start_date)} – {formatDate(detailData.tour_end_date)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Guests</span>
                      <span className="detail-value">{detailData.pax_count}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Food Preferences</span>
                      <span className="detail-value">
                        {detailData.want_veg_food ? 'Veg ' : ''}
                        {detailData.want_nonveg_food ? 'Non‑Veg' : ''}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Contact</span>
                      <span className="detail-value">{detailData.contact?.name} ({detailData.contact?.email})<br />{detailData.contact?.mobile}</span>
                    </div>
                    {detailData.special_note && (
                      <div className="detail-item full-width">
                        <span className="detail-label">Special Note</span>
                        <span className="detail-value">{detailData.special_note}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="detail-section">
                  <h4>Price Breakdown</h4>
                  <div className="price-breakdown">
                    <div className="price-row"><span>Couple Price</span><span>{formatPrice(detailData.couple_price)}</span></div>
                    <div className="price-row"><span>Add‑ons</span><span>{formatPrice(detailData.addons_total)}</span></div>
                    <div className="price-row"><span>Subtotal</span><span>{formatPrice(detailData.subtotal)}</span></div>
                    {detailData.discount_amount > 0 && (
                      <div className="price-row discount"><span>Discount</span><span>-{formatPrice(detailData.discount_amount)}</span></div>
                    )}
                    <div className="price-row"><span>GST ({detailData.gst_percentage}%)</span><span>{formatPrice(detailData.gst_amount)}</span></div>
                    <div className="price-row total"><span>Grand Total</span><span>{formatPrice(detailData.grand_total)}</span></div>
                    <div className="price-row payment">
                      <span>Amount Paid</span>
                      <span>{formatPrice(detailData.payment?.amount_paid || 0)}</span>
                    </div>
                    <div className="price-row balance">
                      <span>Balance Due</span>
                      <span>{formatPrice(detailData.payment?.balance_due || 0)}</span>
                    </div>
                  </div>
                </div>

                {/* Itinerary */}
                {detailData.days && detailData.days.length > 0 && (
                  <div className="detail-section">
                    <h4>Itinerary</h4>
                    <div className="itinerary-days">
                      {detailData.days.map((day) => (
                        <div key={day.day_number} className="itinerary-day">
                          <div className="day-header">
                            <span className="day-number">Day {day.day_number}</span>
                            <span className="day-date">{formatDate(day.day_date)}</span>
                          </div>
                          <h5>{day.title}</h5>
                          <div
                            className="day-description"
                            dangerouslySetInnerHTML={{ __html: day.description }}
                          />
                          {day.hotels && day.hotels.length > 0 && (
                            <div className="day-details">
                              <span className="detail-label"><FiHome /> Hotels</span>
                              {day.hotels.map((hotel, idx) => (
                                <span key={idx}>{hotel.hotel_name} – {hotel.room_name}</span>
                              ))}
                            </div>
                          )}
                          {day.transports && day.transports.length > 0 && (
                            <div className="day-details">
                              <span className="detail-label"><FiTruck /> Transports</span>
                              {day.transports.map((t, idx) => (
                                <span key={idx}>{t.transport_name}</span>
                              ))}
                            </div>
                          )}
                          {day.addons && day.addons.length > 0 && (
                            <div className="day-details">
                              <span className="detail-label"><FiPackage /> Add‑ons</span>
                              {day.addons.map((a, idx) => (
                                <span key={idx}>{a.addon_name} (×{a.quantity})</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="detail-actions">
                  {detailData.booking_status !== 'cancelled' && (
                    <>
                      {detailData.payment_status === 'pending' && (
                        <button
                          className="btn-pay"
                          onClick={() => {
                            closeDetailModal();
                            handlePayNow(selectedBooking || { booking_id: detailData.booking_id });
                          }}
                        >
                          Pay Now
                        </button>
                      )}
                      <button
                        className="btn-invoice"
                        onClick={() => handleDownloadInvoice(detailData)}
                      >
                        <FiDownload /> Download Invoice
                      </button>
                      <button
                        className="btn-cancel"
                        onClick={() => handleCancelClick(selectedBooking || detailData)}
                      >
                        <FiTrash2 /> Cancel Booking
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="detail-error">
                <FiAlertCircle className="error-icon" />
                <p>Could not load booking details.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Payment Modal ──────────────────────────────────────────── */}
      {showPaymentModal && selectedBooking && (
        <div className="payment-modal-overlay" onClick={onPaymentDismiss}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <button className="payment-modal-close" onClick={onPaymentDismiss}>
              <FiX />
            </button>
            <div className="payment-modal-header">
              <h2>Complete Payment</h2>
              <p>Booking #{selectedBooking.booking_code}</p>
            </div>
            <div className="payment-summary">
              <div className="payment-amount">
                <span>Amount Due</span>
                <strong>{formatPrice(selectedBooking.balance_due || selectedBooking.grand_total)}</strong>
              </div>
            </div>

            {paymentStep === 'creating' && (
              <div className="payment-status processing">
                <FiLoader className="spinner" />
                <p>Preparing payment...</p>
              </div>
            )}
            {paymentStep === 'processing' && (
              <div className="payment-status processing">
                <FiLoader className="spinner" />
                <p>Processing payment...</p>
              </div>
            )}
            {paymentStep === 'completed' && (
              <div className="payment-status success">
                <FiCheckCircle className="success-icon" />
                <p>Payment successful! Your booking is confirmed.</p>
              </div>
            )}
            {paymentStep === 'failed' && (
              <div className="payment-status failed">
                <FiAlertCircle className="error-icon" />
                <p>{paymentError || 'Payment failed. Please try again.'}</p>
                <button className="btn-retry" onClick={handleInitiatePayment}>
                  Retry
                </button>
              </div>
            )}

            {paymentStep === 'idle' && (
              <button className="btn-pay-now" onClick={handleInitiatePayment}>
                Pay Now
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── Cancel Confirmation Modal ────────────────────────────── */}
      {showCancelModal && selectedBooking && (
        <div className="cancel-modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="cancel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cancel-modal-header">
              <h3>Cancel Booking</h3>
              <button className="cancel-modal-close" onClick={() => setShowCancelModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="cancel-modal-body">
              <p>
                Are you sure you want to cancel booking <strong>#{selectedBooking.booking_code}</strong>?
              </p>
              <div className="cancel-reason-group">
                <label htmlFor="cancelReason">
                  Reason for cancellation <span>(optional)</span>
                </label>
                <textarea
                  id="cancelReason"
                  rows="3"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g., Change of plans, postponing, etc."
                />
              </div>
              <p className="cancel-note">This action cannot be undone.</p>
            </div>
            <div className="cancel-modal-actions">
              <button className="btn-secondary" onClick={() => setShowCancelModal(false)}>
                No, Go Back
              </button>
              <button className="btn-danger" onClick={confirmCancel} disabled={cancelling}>
                {cancelling ? <FiLoader className="spinner" /> : 'Yes, Cancel Booking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HoneymoonBookingHistory;